import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SeoHelmet (single component for both indexable + non-indexable pages)
 *
 * Props:
 *  title, description, url, image, keywords[], twitterHandle
 *  schemaAuto (bool), schemaType ("tool"|"page"), appCategory, softwareVersion, offers
 *  breadcrumbs[{ name, url }], schemaExtra
 *
 *  indexable         boolean   // default: true. If false: robots=noindex,nofollow and suppress canonical/OG/Twitter/JSON-LD
 *  robotsOverride    string    // optional: e.g., "noindex,follow" (wins over indexable)
 *  ogType            string    // default: "website" (use "article" for posts)
 *  locale            string    // default: "en_US" (og:locale)
 *  twitterCard       string    // default: "summary_large_image"
 */
export default function SeoHelmet({
  title,
  description,
  url,
  image,
  keywords = [],
  twitterHandle = '@geocalculations',

  schemaAuto = true,
  schemaType = 'page',
  appCategory = 'EngineeringApplication',
  softwareVersion,
  offers,

  breadcrumbs = [],
  schemaExtra,

  // new controls
  indexable = true,
  robotsOverride,
  ogType = 'website',
  locale = 'en_US',
  twitterCard = 'summary_large_image',
}) {
  // --- safe title handling ---
  const safeTitle = title || 'GeoCalc';
  const fullTitle = safeTitle.includes('GeoCalc') ? safeTitle : `${safeTitle} | GeoCalc`;

  // ---- JSON-LD builders ----
  const buildWebPage = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: fullTitle,
    url,
    description,
    image,
    isPartOf: {
      '@type': 'WebSite',
      name: 'GeoCalc',
      url: 'https://www.geocalc.app',
    },
  });

  const buildSoftwareApplication = () => {
    const base = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: title,
      applicationCategory: appCategory,
      operatingSystem: 'All',
      url,
      description,
      image,
      publisher: {
        '@type': 'Organization',
        name: 'GeoCalc',
        url: 'https://www.geocalc.app',
      },
    };
    if (softwareVersion) base.softwareVersion = softwareVersion;
    if (offers && (offers.price || offers.category || offers.priceCurrency)) {
      base.offers = { '@type': 'Offer', ...offers };
    }
    return base;
  };

  const buildBreadcrumbs = () =>
    breadcrumbs.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: b.name,
            item: b.url,
          })),
        }
      : null;

  // Schema emission is disabled when not indexable or no canonical URL
  const shouldEmitSchema = indexable && schemaAuto && !!url;

  const autoSchemas = [];
  if (shouldEmitSchema) {
    autoSchemas.push(schemaType === 'tool' ? buildSoftwareApplication() : buildWebPage());
    const bc = buildBreadcrumbs();
    if (bc) autoSchemas.push(bc);
  }

  const extraSchemas = Array.isArray(schemaExtra) ? schemaExtra : schemaExtra ? [schemaExtra] : [];
  const schemas = [...autoSchemas, ...extraSchemas];

  // Robots logic
  const robots = typeof robotsOverride === 'string' ? robotsOverride : indexable ? 'index, follow' : 'noindex, nofollow';

  // When not indexable, suppress canonical + OG/Twitter to match your rule
  const emitSocial = indexable;

  return (
    <Helmet>
      {/* ===== Basic ===== */}
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <meta name="robots" content={robots} />
      {emitSocial && url && <link rel="canonical" href={url} />}

      {/* ===== Open Graph ===== */}
      {emitSocial && (
        <>
          <meta property="og:type" content={ogType} />
          {fullTitle && <meta property="og:title" content={fullTitle} />}
          {description && <meta property="og:description" content={description} />}
          {url && <meta property="og:url" content={url} />}
          {image && <meta property="og:image" content={image} />}
          <meta property="og:site_name" content="GeoCalc" />
          <meta property="og:locale" content={locale} />
        </>
      )}

      {/* ===== Twitter ===== */}
      {emitSocial && (
        <>
          <meta name="twitter:card" content={twitterCard} />
          {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
          {fullTitle && <meta name="twitter:title" content={fullTitle} />}
          {description && <meta name="twitter:description" content={description} />}
          {image && <meta name="twitter:image" content={image} />}
        </>
      )}

      {/* ===== JSON-LD ===== */}
      {shouldEmitSchema &&
        schemas.map((obj, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}
    </Helmet>
  );
}
